import { convertDateRangeToDates } from "@wingspanhq/fe-component-library";
import { ListPayablesRequest } from "../../../../../services/api/payments/payables";
import { UrlQuerySort } from "../../../../../shared/hooks/useUrlQuery";
import { getApiDirectionFromTable } from "../../../../../shared/utils/sorting";
import { Filter, PayablesTabFilter, SortField } from "./types";

type ListPayablesRequestBody = Omit<
  ListPayablesRequest,
  "page[mode]" | "page[size]" | "page[token]" | "page[resultSetId]"
>;

export function getTabFilter(
  tab: PayablesTabFilter,
  isDualStage = false
): ListPayablesRequestBody {
  switch (tab) {
    case "draft":
      return { "filter[status][in]": ["Draft"] };
    case "to-approve":
      return isDualStage
        ? {
            "filter[status][in]": ["Pending", "Open", "Overdue"],
            excludeDeclined: "true",
            "filter[payer.workflowStatus][in]": ["PreApproved"]
          }
        : {
            "filter[status][in]": ["Pending", "Open", "Overdue"],
            excludeDeclined: "true",
            "filter[payer.workflowStatus][notIn]": [
              "Approved",
              "PaymentInitiated",
              "Funded"
            ]
          };
    case "to-preapprove":
      return {
        "filter[status][in]": ["Pending", "Open", "Overdue"],
        excludeDeclined: "true",
        "filter[payer.workflowStatus][notIn]": [
          "PreApproved",
          "Approved",
          "PaymentInitiated",
          "Funded"
        ]
      };
    case "scheduled":
      return {
        "filter[status][in]": ["Pending", "Open", "Overdue"],
        "filter[payer.workflowStatus][in]": ["Approved", "PaymentInitiated"]
      };
    case "paid":
      return { "filter[status][in]": ["Paid", "PaymentInTransit"] };
    case "all":
      return {};
    default:
      return {};
  }
}

export function createListRequest(
  _userId: string,
  filter: Filter | undefined,
  sort: UrlQuerySort<SortField> | undefined,
  tab: PayablesTabFilter,
  isDualStage = false
): ListPayablesRequestBody {
  let request: ListPayablesRequestBody = {};

  // Status from tab.
  const tabFilter = getTabFilter(tab, isDualStage);
  request = tabFilter;

  if (filter?.payeeId) {
    request["filter[payeeId][=]"] = filter.payeeId;
  }

  if (filter?.dueDateRange) {
    const range = convertDateRangeToDates(filter.dueDateRange);
    if (range?.start) {
      request["filter[dueDate][>=]"] = range.start;
    }
    if (range?.end) {
      request["filter[dueDate][<=]"] = range.end;
    }
  }

  if (filter?.paidDateRange) {
    const range = convertDateRangeToDates(filter.paidDateRange);
    if (range?.start) {
      request["filter[events.paidAt][>]"] = range.start;
    }
    if (range?.end) {
      request["filter[events.paidAt][<]"] = range.end;
    }
  }

  if (filter?.openedDateRange) {
    const range = convertDateRangeToDates(filter.openedDateRange);
    if (range?.start) {
      request["filter[events.openedAt][>]"] = range.start;
    }
    if (range?.end) {
      request["filter[events.openedAt][<]"] = range.end;
    }
  }

  // Sort.
  const direction = getApiDirectionFromTable(sort?.direction);
  if (sort && direction) {
    if (sort.field === "createdAt") {
      request["sort[createdAt]"] = direction;
    } else if (sort.field === "updatedAt") {
      request["sort[updatedAt]"] = direction;
    } else if (sort.field === "events.paidAt") {
      request["sort[events.paidAt]"] = direction;
    }
  } else {
    request["sort[updatedAt]"] = "desc";
  }

  return request;
}
